import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  TextField,
  Grid,
  Alert,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import axios from 'axios';

/**
 * Create School Page (SuperAdmin)
 */
const CreateSchool: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    isActive: true,
    // SuperAdmin managed fields
    billingName: '',
    accountStartYear: new Date().getFullYear(),
    sessionStartMonth: 4, // April default
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post('/schools', formData);
      // Trigger dashboard refresh immediately
      window.dispatchEvent(new Event('schoolUpdated'));
      navigate('/superadmin/schools');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create school');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <Container maxWidth="md">
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/superadmin/schools')}
            sx={{ mb: 2 }}
          >
            Back to Schools
          </Button>
          <Typography variant="h4">Create School</Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="School Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="School Code"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Email Address"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  required
                  label="Phone No"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PIN"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Billing Name"
                  name="billingName"
                  value={formData.billingName}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Account Start Year"
                  name="accountStartYear"
                  value={formData.accountStartYear}
                  onChange={handleChange}
                  disabled={loading}
                  inputProps={{ min: 2000, max: 2100 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Session Start Month"
                  name="sessionStartMonth"
                  value={formData.sessionStartMonth}
                  onChange={handleChange}
                  disabled={loading}
                  inputProps={{ min: 1, max: 12 }}
                  helperText="Month number (1-12)"
                />
              </Grid>

              {/* <Grid item xs={12}>
                <Typography variant="h6">Institute Profile</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Reg./Aff. No"
                  name="regAffNo"
                  value={instituteData.regAffNo}
                  onChange={(e) => setInstituteData({ ...instituteData, regAffNo: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Principal Name"
                  name="principalName"
                  value={instituteData.principalName}
                  onChange={(e) => setInstituteData({ ...instituteData, principalName: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address 1"
                  name="address1"
                  value={instituteData.address1}
                  onChange={(e) => setInstituteData({ ...instituteData, address1: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address 2"
                  name="address2"
                  value={instituteData.address2}
                  onChange={(e) => setInstituteData({ ...instituteData, address2: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="District"
                  name="district"
                  value={instituteData.district}
                  onChange={(e) => setInstituteData({ ...instituteData, district: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="PIN"
                  name="pin"
                  value={instituteData.pin}
                  onChange={(e) => setInstituteData({ ...instituteData, pin: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Website"
                  name="websiteName"
                  value={instituteData.websiteName}
                  onChange={(e) => setInstituteData({ ...instituteData, websiteName: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="emailAddress"
                  value={instituteData.emailAddress}
                  onChange={(e) => setInstituteData({ ...instituteData, emailAddress: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Phone No"
                  name="phoneNo"
                  value={instituteData.phoneNo}
                  onChange={(e) => setInstituteData({ ...instituteData, phoneNo: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Mobile"
                  name="mobile"
                  value={instituteData.mobile}
                  onChange={(e) => setInstituteData({ ...instituteData, mobile: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Scholar No Prefix"
                  name="scholarNoPrefix"
                  value={instituteData.scholarNoPrefix}
                  onChange={(e) => setInstituteData({ ...instituteData, scholarNoPrefix: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Scholar No Suffix"
                  name="scholarNoSuffix"
                  value={instituteData.scholarNoSuffix}
                  onChange={(e) => setInstituteData({ ...instituteData, scholarNoSuffix: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Employee No Prefix"
                  name="empNoPrefix"
                  value={instituteData.empNoPrefix}
                  onChange={(e) => setInstituteData({ ...instituteData, empNoPrefix: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Fax No"
                  name="faxNo"
                  value={instituteData.faxNo}
                  onChange={(e) => setInstituteData({ ...instituteData, faxNo: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Accredited By"
                  name="accreditedBy"
                  value={instituteData.accreditedBy}
                  onChange={(e) => setInstituteData({ ...instituteData, accreditedBy: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Pages in TC Book"
                  name="tcBookPages"
                  value={instituteData.tcBookPages}
                  onChange={(e) => setInstituteData({ ...instituteData, tcBookPages: e.target.value })}
                  disabled={loading}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Background Image URL"
                  name="backgroundImage"
                  value={instituteData.backgroundImage}
                  onChange={(e) => setInstituteData({ ...instituteData, backgroundImage: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  label="Input Text Format"
                  name="inputTextFormat"
                  value={instituteData.inputTextFormat}
                  onChange={(e) => setInstituteData({ ...instituteData, inputTextFormat: e.target.value })}
                  disabled={loading}
                  SelectProps={{ native: true }}
                >
                  <option value="Capitalize">Capitalize</option>
                  <option value="Title Case">Title Case</option>
                  <option value="Upper Case">Upper Case</option>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                  Feature Settings
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.autoEnrollmentNo}
                          onChange={(e) => setInstituteData({ ...instituteData, autoEnrollmentNo: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Auto Enrollment No"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.autoAdmissionNumber}
                          onChange={(e) => setInstituteData({ ...instituteData, autoAdmissionNumber: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Auto Admission No"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.autoScholarNumber}
                          onChange={(e) => setInstituteData({ ...instituteData, autoScholarNumber: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Auto Scholar No"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.autoEmployeeNo}
                          onChange={(e) => setInstituteData({ ...instituteData, autoEmployeeNo: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Auto Employee No"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.suggestAutoSection}
                          onChange={(e) => setInstituteData({ ...instituteData, suggestAutoSection: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Suggest Auto Section"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.suggestAutoHouse}
                          onChange={(e) => setInstituteData({ ...instituteData, suggestAutoHouse: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Suggest Auto House"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.singleLogin}
                          onChange={(e) => setInstituteData({ ...instituteData, singleLogin: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Single Login"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.loginWithSingleDevice}
                          onChange={(e) => setInstituteData({ ...instituteData, loginWithSingleDevice: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Login Single Device"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.changeZoomUrl}
                          onChange={(e) => setInstituteData({ ...instituteData, changeZoomUrl: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Change Zoom URL"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.liveClassShowOnTime}
                          onChange={(e) => setInstituteData({ ...instituteData, liveClassShowOnTime: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Live Class On Time"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.liveClassJoinLogOfStudent}
                          onChange={(e) => setInstituteData({ ...instituteData, liveClassJoinLogOfStudent: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Log Student Join"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.studentAttendanceThroughAssignment}
                          onChange={(e) => setInstituteData({ ...instituteData, studentAttendanceThroughAssignment: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Attend. via Assgn"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.studentAttendanceThroughLiveClass}
                          onChange={(e) => setInstituteData({ ...instituteData, studentAttendanceThroughLiveClass: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Attend. via Live Class"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.allowToEditEmpAttendanceTime}
                          onChange={(e) => setInstituteData({ ...instituteData, allowToEditEmpAttendanceTime: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Edit Emp Attendance"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.sendStudentWelcomeMsgToClassTeacher}
                          onChange={(e) => setInstituteData({ ...instituteData, sendStudentWelcomeMsgToClassTeacher: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Welcome Msg to Teacher"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.showStudentContactNoInApp}
                          onChange={(e) => setInstituteData({ ...instituteData, showStudentContactNoInApp: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Show Student Contact"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.showTeacherContactNoInApp}
                          onChange={(e) => setInstituteData({ ...instituteData, showTeacherContactNoInApp: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Show Teacher Contact"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.allowScreenshotsApp}
                          onChange={(e) => setInstituteData({ ...instituteData, allowScreenshotsApp: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Allow Screenshots"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.showYoutubeOptionsApp}
                          onChange={(e) => setInstituteData({ ...instituteData, showYoutubeOptionsApp: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Show Youtube Options"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.viewClassTestResultInMobileAppWithoutPublish}
                          onChange={(e) => setInstituteData({ ...instituteData, viewClassTestResultInMobileAppWithoutPublish: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="View Test Result (Unpub)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.showExamListResultWise}
                          onChange={(e) => setInstituteData({ ...instituteData, showExamListResultWise: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Show Exam List Result Wise"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.hideResultInMobileAppWhenCurrentFeeDue}
                          onChange={(e) => setInstituteData({ ...instituteData, hideResultInMobileAppWhenCurrentFeeDue: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Hide Result if Fee Due"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={instituteData.autoRegisterForMobileApp}
                          onChange={(e) => setInstituteData({ ...instituteData, autoRegisterForMobileApp: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Auto Register App"
                    />
                  </Grid>
                </Grid>
              </Grid> */}

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/superadmin/schools')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create School'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </MainLayout>
  );
};

export default CreateSchool;
